import {
  DefaultInspirationPerPage,
  DetailedInspiration,
  DetailedInspirationItem,
  Inspiration,
  InspirationItemSummary,
  InspirationWhere,
} from "@/types/inspiration"
import { Place, PlacePhoto, PlacePlaceholder } from "@/types/place"
import {
  CaseReducer,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit"

import { isDetailedEntity } from "../entity-utils"
import { RootState } from "./redux-store"
import userSlice, { selectUser, selectUserPermissions } from "./user-slice"

export interface InspirationSliceState {
  currInspiration: Inspiration | undefined
  currInspirationItem: DetailedInspirationItem | undefined
  sheetExpanded: boolean
  coverImage: boolean
  editDialogMode: "opened" | "closed"
  editImageDialogMode: "opened" | "closed"
  editImageSourceDialogMode: "opened" | "closed"
  selectImagesDialogMode: "opened" | "closed"
  deleteDayDialogMode: "opened" | "closed"
  moveItemDialogMode: "opened" | "closed"
  generateDescriptionDialogMode: "opened" | "closed"
  blockAddLinkDialogMode: "opened" | "closed"
  blockAddGuideDialogMode: "opened" | "closed"
  editAudioDialogMode: "opened" | "closed"
  publishDialogMode: "opened" | "closed"
  editWhere: InspirationWhere | undefined
  editText: string | undefined
  editDayIndex: number | undefined
  placePhoto: PlacePhoto | undefined

  // Generating guide - streaming
  generateInspirationAfterStream: boolean
  generateInspiration: string | undefined

  loading: boolean
  fetchingMoreItems: boolean
  hasMoreInspirations: boolean
  currentPage: number
  maxPage: number
  searchTerm: string
  perPage: number

  // Unified search - adding operation: add item, insert an item, or update an existing item
  placeAddOperation:
    | "add-block"
    | "insert-block"
    | "insert-block-before"
    | "update-block"
    | undefined

  // Unified search - place to remove from inspiration
  placeToRemove: Place | undefined
}

export const inspirationSliceInitialState: InspirationSliceState = {
  currInspiration: undefined,
  currInspirationItem: undefined,
  sheetExpanded: true,
  coverImage: false,
  editDialogMode: "closed",
  editImageDialogMode: "closed",
  editImageSourceDialogMode: "closed",
  selectImagesDialogMode: "closed",
  deleteDayDialogMode: "closed",
  moveItemDialogMode: "closed",
  generateDescriptionDialogMode: "closed",
  blockAddLinkDialogMode: "closed",
  blockAddGuideDialogMode: "closed",
  editAudioDialogMode: "closed",
  publishDialogMode: "closed",
  editWhere: undefined,
  editText: undefined,
  editDayIndex: undefined,
  placePhoto: undefined,
  generateInspiration: undefined,
  generateInspirationAfterStream: false,
  loading: false,
  fetchingMoreItems: false,
  hasMoreInspirations: true,
  currentPage: 1,
  maxPage: 1,
  searchTerm: "",
  perPage: DefaultInspirationPerPage,
  placeAddOperation: undefined,
  placeToRemove: undefined,
}

export const selectInspirationState = (state: RootState) =>
  state.inspirationSlice

export const selectCurrInspiration = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.currInspiration
)

/**
 * The store's copy of a guide when it holds that guide in detail, else the
 * given one. A save that computes the next guide from this copy keeps the
 * results of other saves that landed in the meantime (a block move and a
 * text edit do not overwrite each other).
 */
export const selectLatestDetailedInspiration = (
  state: RootState,
  fallback: DetailedInspiration
): DetailedInspiration => {
  const current = selectCurrInspiration(state)
  return current &&
    isDetailedEntity(current) &&
    current.entity_ref === fallback.entity_ref
    ? current
    : fallback
}

export const selectCurrInspirationRef = createSelector(
  [selectCurrInspiration],
  (inspiration) => inspiration?.entity_ref
)

export const selectCurrInspirationItem = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.currInspirationItem
)

// permissions
export const selectCanPublishInspirationPermission = createSelector(
  [selectUserPermissions],
  (permissions) => !!permissions?.includes("can_publish_inspiration")
)

export const selectCanEditInspirationPermission = createSelector(
  [selectUserPermissions, selectCurrInspiration, selectUser],
  (permissions, inspiration, currUser) =>
    permissions?.includes("can_publish_inspiration") &&
    inspiration?.owner.handle === currUser.handle
)

export const selectCanAdminInspirationPermission = createSelector(
  [selectUserPermissions],
  (permissions) => !!permissions?.includes("can_admin_inspiration")
)

export const selectInspirationSheetExpanded = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.sheetExpanded
)

export const selectCoverImage = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.coverImage
)

export const selectIsInspirationEditDialogOpen = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.editDialogMode === "opened"
)

export const selectIsInspirationEditImageDialogOpen = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.editImageDialogMode === "opened"
)

export const selectIsInspirationEditImageSourceDialogOpen = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.editImageSourceDialogMode === "opened"
)

export const selectIsInspirationSelectImagesDialogOpen = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.selectImagesDialogMode === "opened"
)

export const selectIsInspirationDeleteDayDialogOpen = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.deleteDayDialogMode === "opened"
)

export const selectIsInspirationBlockAddLinkDialogOpen = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.blockAddLinkDialogMode === "opened"
)

export const selectIsInspirationBlockAddGuideDialogOpen = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.blockAddGuideDialogMode === "opened"
)

export const selectIsInspirationEditAudioDialogOpen = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.editAudioDialogMode === "opened"
)

export const selectIsInspirationMoveItemDialogOpen = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.moveItemDialogMode === "opened"
)

export const selectIsGenerateDescriptionDialogOpen = createSelector(
  [selectInspirationState],
  (inspirationState) =>
    inspirationState.generateDescriptionDialogMode === "opened"
)

export const selectIsBlockAddGuideDialogOpen = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.blockAddGuideDialogMode === "opened"
)

export const selectIsInspirationPublishDialogOpen = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.publishDialogMode === "opened"
)

export const selectInspirationEditWhere = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.editWhere
)

export const selectInspirationEditText = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.editText
)

export const selectInspirationEditDayIndex = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.editDayIndex
)

export const selectCurrInspirationPlacePhoto = createSelector(
  [selectInspirationState],
  (inspirationState) => inspirationState.placePhoto
)

export const selectSearchTerm = createSelector(
  [selectInspirationState],
  (state) => state.searchTerm
)

export const selectInspirationPerPage = createSelector(
  [selectInspirationState],
  (state) => state.perPage
)

export const selectInspirationPlaceAddOperation = createSelector(
  [selectInspirationState],
  (state) => state.placeAddOperation
)

export const selectInspirationPlaceToRemove = createSelector(
  [selectInspirationState],
  (state) => state.placeToRemove
)

export const selectLoading = createSelector(
  [selectInspirationState],
  (state) => state.loading
)

export const selectFetchingMoreItems = createSelector(
  [selectInspirationState],
  (state) => state.fetchingMoreItems
)

export const selectHasMoreInspirations = createSelector(
  [selectInspirationState],
  (state) => state.hasMoreInspirations
)

export const selectCurrentPage = createSelector(
  [selectInspirationState],
  (state) => state.currentPage
)

export const selectMaxPage = createSelector(
  [selectInspirationState],
  (state) => state.maxPage
)

export const selectIsPlaceInInspiration = createSelector(
  [
    selectCurrInspiration,
    (_state: RootState, place: Place | PlacePlaceholder) => place,
  ],
  (inspiration, place) => {
    if (inspiration?.detailed) {
      const found = inspiration.items.find(
        (item) => item.detailed && item.place?.entity_ref === place.entity_ref
      )
      return found !== undefined
    }
    return false
  }
)

const setCurrInspiration: CaseReducer<
  InspirationSliceState,
  PayloadAction<Inspiration | undefined>
> = (state, action) => {
  state.currInspiration = action.payload
}

const setCurrInspirationItem: CaseReducer<
  InspirationSliceState,
  PayloadAction<DetailedInspirationItem | undefined>
> = (state, action) => {
  state.currInspirationItem = action.payload
}

const setInspirationSheetExpanded: CaseReducer<
  InspirationSliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.sheetExpanded = action.payload
}

const setCoverImage: CaseReducer<
  InspirationSliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.coverImage = action.payload
}

const openInspirationEditDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.editDialogMode = "opened"
}

const closeInspirationEditDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.editDialogMode = "closed"
}

const openInspirationEditImageDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.editImageDialogMode = "opened"
}

const closeInspirationEditImageDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.editImageDialogMode = "closed"
}

const openInspirationEditImageSourceDialog: CaseReducer<
  InspirationSliceState
> = (state) => {
  state.editImageSourceDialogMode = "opened"
}

const closeInspirationEditImageSourceDialog: CaseReducer<
  InspirationSliceState
> = (state) => {
  state.editImageSourceDialogMode = "closed"
}

const openInspirationSelectImagesDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.selectImagesDialogMode = "opened"
}

const closeInspirationSelectImagesDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.selectImagesDialogMode = "closed"
}

const openInspirationDeleteDayDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.deleteDayDialogMode = "opened"
}

const closeInspirationDeleteDayDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.deleteDayDialogMode = "closed"
}

const openInspirationMoveItemDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.moveItemDialogMode = "opened"
}

const closeInspirationMoveItemDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.moveItemDialogMode = "closed"
}

const openInspirationGenerateDescriptionDialog: CaseReducer<
  InspirationSliceState
> = (state) => {
  state.generateDescriptionDialogMode = "opened"
}

const closeInspirationGenerateDescriptionDialog: CaseReducer<
  InspirationSliceState
> = (state) => {
  state.generateDescriptionDialogMode = "closed"
}

const openInspirationBlockAddLinkDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.blockAddLinkDialogMode = "opened"
}

const closeInspirationBlockAddLinkDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.blockAddLinkDialogMode = "closed"
}

const openInspirationBlockAddGuideDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.blockAddGuideDialogMode = "opened"
}

const closeInspirationBlockAddGuideDialog: CaseReducer<
  InspirationSliceState
> = (state) => {
  state.blockAddGuideDialogMode = "closed"
}

const openInspirationEditAudioDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.editAudioDialogMode = "opened"
}

const closeInspirationEditAudioDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.editAudioDialogMode = "closed"
}

const openInspirationPublishDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.publishDialogMode = "opened"
}

const closeInspirationPublishDialog: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.publishDialogMode = "closed"
}

const setInspirationEditWhere: CaseReducer<
  InspirationSliceState,
  PayloadAction<InspirationWhere>
> = (state, action) => {
  state.editWhere = action.payload
}

const setInspirationEditText: CaseReducer<
  InspirationSliceState,
  PayloadAction<string>
> = (state, action) => {
  state.editText = action.payload
}

const setInspirationEditDayIndex: CaseReducer<
  InspirationSliceState,
  PayloadAction<number>
> = (state, action) => {
  state.editDayIndex = action.payload
}

const setCurrInspirationPlacePhoto: CaseReducer<
  InspirationSliceState,
  PayloadAction<PlacePhoto | undefined>
> = (state, action) => {
  state.placePhoto = action.payload
}

const appendUnloadedInspirationItem: CaseReducer<
  InspirationSliceState,
  PayloadAction<{
    detailedInspiration: DetailedInspiration
    index: number
    items: InspirationItemSummary[]
  }>
> = (state, action) => {
  if (
    state.currInspiration &&
    state.currInspiration.entity_ref ===
      action.payload.detailedInspiration.entity_ref &&
    isDetailedEntity(state.currInspiration) &&
    state.currInspiration.items
  ) {
    const index = action.payload.index
    const curr_fetch_count = state.currInspiration.fetch_count
    let additional_fetch_count = action.payload.items.length
    if (curr_fetch_count && index < curr_fetch_count) {
      // Append starting after what we fetched so far
      state.currInspiration.items = state.currInspiration.items.concat(
        ...action.payload.items.slice(curr_fetch_count)
      )
      additional_fetch_count = additional_fetch_count - curr_fetch_count
    } else {
      // Append because we have not fetch this yet
      state.currInspiration.items = state.currInspiration.items.concat(
        ...action.payload.items
      )
    }

    // Update count
    state.currInspiration.fetch_count ||= 0
    state.currInspiration.fetch_count += additional_fetch_count
  }
}

const updateDetailedInspirationItem: CaseReducer<
  InspirationSliceState,
  PayloadAction<DetailedInspirationItem>
> = (state, action) => {
  if (
    state.currInspiration &&
    isDetailedEntity(state.currInspiration) &&
    state.currInspiration?.items
  ) {
    // Next, find where this is in the list
    const index = state.currInspiration.items.findIndex(
      (inspirationItem) => inspirationItem.id === action.payload.id
    )
    if (index !== -1) {
      // Remove and then add to same position (update)
      state.currInspiration.items.splice(index, 1, action.payload)
    }
  }
}

const setGenerateInsprationAfterStream: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.generateInspirationAfterStream = true
}

const clearGenerateInspirationAfterStream: CaseReducer<
  InspirationSliceState
> = (state) => {
  state.generateInspirationAfterStream = false
}

export const selectIsGenerateInspirationAfterStream = createSelector(
  [selectInspirationState],
  (state) => state.generateInspirationAfterStream
)

export const selectGenerateInspirationState = createSelector(
  [selectInspirationState],
  (state) => state.generateInspiration
)

const setGenerateInspirationState: CaseReducer<
  InspirationSliceState,
  PayloadAction<string>
> = (state, action) => {
  state.generateInspiration = action.payload
}

export const selectIsStartGenerateInspiration = createSelector(
  [selectInspirationState],
  (state) => state.generateInspiration === "start"
)

export const selectIsDoneGenerateInspiration = createSelector(
  [selectInspirationState],
  (state) => state.generateInspiration === "done"
)

const clearGenerateInspiration: CaseReducer<InspirationSliceState> = (
  state
) => {
  state.generateInspiration = undefined
}

const setLoading: CaseReducer<InspirationSliceState, PayloadAction<boolean>> = (
  state,
  action
) => {
  state.loading = action.payload
}

const setFetchingMoreItems: CaseReducer<
  InspirationSliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.fetchingMoreItems = action.payload
}

const setPaging: CaseReducer<
  InspirationSliceState,
  PayloadAction<{ currentPage: number; maxPage: number }>
> = (state, action) => {
  state.currentPage = action.payload.currentPage
  state.maxPage = action.payload.maxPage
  state.hasMoreInspirations =
    action.payload.currentPage < action.payload.maxPage
}

const setSearchTerm: CaseReducer<
  InspirationSliceState,
  PayloadAction<string>
> = (state, action) => {
  state.searchTerm = action.payload
}

const setPlaceAddOperation: CaseReducer<
  InspirationSliceState,
  PayloadAction<InspirationSliceState["placeAddOperation"]>
> = (state, action) => {
  state.placeAddOperation = action.payload
}

const setPlaceToRemove: CaseReducer<
  InspirationSliceState,
  PayloadAction<Place | undefined>
> = (state, action) => {
  state.placeToRemove = action.payload
}

const inspirationSlice = createSlice({
  name: "inspirationSlice",
  initialState: inspirationSliceInitialState,
  reducers: {
    setCurrInspiration,
    setCurrInspirationItem,
    setInspirationSheetExpanded,
    setCoverImage,
    setInspirationEditWhere,
    setInspirationEditText,
    setInspirationEditDayIndex,
    setCurrInspirationPlacePhoto,
    openInspirationEditDialog,
    closeInspirationEditDialog,
    openInspirationEditImageDialog,
    closeInspirationEditImageDialog,
    openInspirationEditImageSourceDialog,
    closeInspirationEditImageSourceDialog,
    openInspirationSelectImagesDialog,
    closeInspirationSelectImagesDialog,
    openInspirationDeleteDayDialog,
    closeInspirationDeleteDayDialog,
    openInspirationMoveItemDialog,
    closeInspirationMoveItemDialog,
    openInspirationGenerateDescriptionDialog,
    closeInspirationGenerateDescriptionDialog,
    openInspirationBlockAddLinkDialog,
    closeInspirationBlockAddLinkDialog,
    openInspirationBlockAddGuideDialog,
    closeInspirationBlockAddGuideDialog,
    openInspirationEditAudioDialog,
    closeInspirationEditAudioDialog,
    openInspirationPublishDialog,
    closeInspirationPublishDialog,
    updateDetailedInspirationItem,
    appendUnloadedInspirationItem,

    setGenerateInsprationAfterStream,
    clearGenerateInspirationAfterStream,
    setGenerateInspirationState,
    clearGenerateInspiration,

    setLoading,
    setFetchingMoreItems,
    setPaging,
    setSearchTerm,

    setPlaceAddOperation,
    setPlaceToRemove,
  },
  // Listen for actions that were dispatched to other slices and
  // take related action here in the chat mode slice.
  extraReducers: (builder) => {
    // When message streaming stops, if we were focused on the in-progress
    // message, focus on the final message instead.
    builder
      .addCase(userSlice.actions.stopMessageStreaming, (state) => {
        if (state.generateInspirationAfterStream) {
          state.generateInspiration = "done"
          state.generateInspirationAfterStream = false
        }
      })
      .addCase(userSlice.actions.stopMessageStreamingWithError, (state) => {
        if (state.generateInspirationAfterStream) {
          state.generateInspiration = "error"
          state.generateInspirationAfterStream = false
        }
      })
      .addCase(userSlice.actions.stopMessageStreamingAndDisable, (state) => {
        if (state.generateInspirationAfterStream) {
          state.generateInspiration = "error"
          state.generateInspirationAfterStream = false
        }
      })
      .addCase(userSlice.actions.stopMessageStreamingWithCancel, (state) => {
        if (state.generateInspirationAfterStream) {
          state.generateInspiration = "error"
          state.generateInspirationAfterStream = false
        }
      })
  },
})

export type InspirationSlice = typeof inspirationSlice

export default inspirationSlice
